import React, { useState, useEffect } from "react";
import { useServices } from "../context/ServiceContext";
import ServiceCard from "../components/ServiceCard";
import MainLayout from "../layouts/MainLayout";
import { Box, Typography, Card, keyframes } from "@mui/material";
import { serviceStateEnum } from "../interfaces/servicesTypes";

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export default function Dashboard() {
  const { services: serviceList, selectedServiceId, setSelectedServiceId, selectedService } = useServices();
  const [isLoaded, setIsLoaded] = useState(false);
  const visibleServices = serviceList.filter((service) => service.serviceState !== serviceStateEnum.RETIRED);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <MainLayout>
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f8fafc 0%, #f0f4f8 100%)",
          pb: 6,
          px: { xs: 0, sm: 3, md: 4 },
          pt: { xs: 3, sm: 4 },
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              url("data:image/svg+xml,%3Csvg width='120' height='120' viewBox='0 0 120 120' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='netGrad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%233b82f6;stop-opacity:0.08' /%3E%3Cstop offset='100%25' style='stop-color:%234f46e5;stop-opacity:0.06' /%3E%3C/linearGradient%3E%3C/defs%3E%3C!-- Nodos principales --%3E%3Ccircle cx='20' cy='20' r='4' fill='%233b82f6' opacity='0.15' /%3E%3Ccircle cx='100' cy='30' r='3' fill='%234f46e5' opacity='0.12' /%3E%3Ccircle cx='60' cy='60' r='4.5' fill='%233b82f6' opacity='0.14' /%3E%3Ccircle cx='20' cy='100' r='3' fill='%234f46e5' opacity='0.11' /%3E%3Ccircle cx='100' cy='90' r='4' fill='%233b82f6' opacity='0.13' /%3E%3Ccircle cx='40' cy='40' r='3' fill='%234f46e5' opacity='0.1' /%3E%3Ccircle cx='80' cy='70' r='3.5' fill='%233b82f6' opacity='0.12' /%3E%3C!-- Líneas de conexión --%3E%3Cline x1='20' y1='20' x2='40' y2='40' stroke='%233b82f6' stroke-width='1' opacity='0.08' /%3E%3Cline x1='40' y1='40' x2='60' y2='60' stroke='%234f46e5' stroke-width='1' opacity='0.07' /%3E%3Cline x1='60' y1='60' x2='80' y2='70' stroke='%233b82f6' stroke-width='1' opacity='0.08' /%3E%3Cline x1='100' cy='30' x2='80' y2='70' stroke='%234f46e5' stroke-width='1' opacity='0.07' /%3E%3Cline x1='20' y1='100' x2='60' y2='60' stroke='%233b82f6' stroke-width='1' opacity='0.08' /%3E%3Cline x1='60' y1='60' x2='100' y2='90' stroke='%234f46e5' stroke-width='1' opacity='0.07' /%3E%3Cline x1='20' y1='20' x2='100' y2='90' stroke='%233b82f6' stroke-width='0.8' opacity='0.05' /%3E%3C/svg%3E")
            `,
            backgroundSize: "120px 120px",
            pointerEvents: "none",
            zIndex: 0,
          },
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: "auto", position: "relative", zIndex: 1 }}>
          {/* Mis Servicios Card */}
          <Card
            sx={{
              bgcolor: "hsl(0 0% 100%)",
              borderRadius: "16px",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
              border: "1px solid hsl(220 13% 91%)",
              overflow: "hidden",
              animation: isLoaded ? `${fadeIn} 0.6s ease-in-out` : "none",
            }}
          >
            {/* Card Header */}
            <Box
              sx={{
                px: { xs: 4, sm: 4 },
                py: { xs: 3, sm: 4 },
                borderBottom: "1px solid hsl(220 13% 91%)",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "hsl(240 5.9% 10%)",
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  mb: 1,
                }}
              >
                Mis servicios
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "hsl(240 3.8% 46.1%)",
                  fontSize: { xs: "0.875rem", sm: "0.9rem" },
                  fontWeight: 400,
                }}
              >
                {visibleServices.length} {visibleServices.length === 1 ? "servicio contratado" : "servicios contratados"}
              </Typography>
            </Box>

            {/* Card Content */}
            <Box
              sx={{
                px: { xs: 4, sm: 4 },
                py: { xs: 3, sm: 4 },
              }}
            >
              {/* Subtitle */}
              <Typography
                variant="body1"
                sx={{
                  color: "hsl(240 3.8% 46.1%)",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  fontWeight: 400,
                  mb: 4,
                }}
              >
                Selecciona un servicio para ver más detalles
              </Typography>

              {/* Services List */}
              {visibleServices.length > 0 ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(auto-fill, minmax(280px, 1fr))", lg: "repeat(auto-fill, minmax(350px, 1fr))" },
                    gap: { xs: 1.5, sm: 3 },
                    alignItems: "stretch",
                  }}
                >
                  {visibleServices.map((s, idx) => {
                    const id = String(s._id ?? s.documentNumber ?? idx);
                    return (
                      <Box
                        key={id}
                        sx={{
                          animation: isLoaded ? `${fadeIn} 0.6s ease-in-out` : "none",
                          animationDelay: isLoaded ? `${idx * 0.1}s` : "0s",
                        }}
                      >
                        <ServiceCard
                          service={s}
                          selected={id === selectedServiceId}
                          onClick={() => setSelectedServiceId(id)}
                        />
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 8,
                    px: 4,
                    bgcolor: "hsl(240 4.8% 95.9%)",
                    borderRadius: "12px",
                    border: "1px solid hsl(240 5.9% 90%)",
                    animation: isLoaded ? `${fadeIn} 0.6s ease-in-out` : "none",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: "hsl(240 3.8% 46.1%)",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    No hay servicios disponibles
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "hsl(240 3.8% 46.1%)",
                      opacity: 0.8,
                    }}
                  >
                    Contacta con soporte para contratar un servicio
                  </Typography>
                </Box>
              )}
            </Box>
          </Card>
        </Box>
      </Box>
    </MainLayout>
  );
}
